/**
 * Profile card: catch-up (backfill) overview.
 *
 * Makes backfill configuration discoverable: one row per check-in habit from
 * GET /api/check-ins/queue/insight — mode, policy, effective reach, what is
 * owed, and a short human reason for why the queue is (not) offering catch-up
 * work today. Habits with backfill off sort last; the headline counts them,
 * because "most of my habits silently have this off" is the insight the queue
 * itself can never surface.
 *
 * Strictly read-only and deliberately quiet: a fetch error or an account with
 * no check-in habits renders nothing at all — this card explains, it never
 * demands attention.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Chip, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';

import {
  CHECK_IN_INSIGHT_PATH,
  CheckInQueueInsightHabit,
  CheckInQueueInsightResponse,
} from '../checkin/insightTypes';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const MODE_LABELS: Record<CheckInQueueInsightHabit['backfill_mode'], string> = {
  none: 'Off',
  retroactive_log: 'Retro log',
  debt_carry_forward: 'Debt',
};

const MODE_CHIP_COLORS: Record<
  CheckInQueueInsightHabit['backfill_mode'],
  'default' | 'secondary' | 'warning'
> = {
  none: 'default',
  retroactive_log: 'secondary',
  debt_carry_forward: 'warning',
};

const POLICY_LABELS: Record<CheckInQueueInsightHabit['carryover_policy'], string> = {
  stack: 'Stack',
  merge: 'Merge',
};

/** "3.0" for whole amounts (owed is period arithmetic), "2.25" otherwise. */
const formatOwedAmount = (amount: number): string =>
  Number.isInteger(amount) ? amount.toFixed(1) : String(amount);

/** The ratified reason vocabulary, spoken plainly. Unknown codes (a newer
 * backend) render no sentence rather than a raw enum. */
const reasonSentence = (habit: CheckInQueueInsightHabit): string => {
  switch (habit.reason) {
    case 'mode_none':
      return 'backfill is off';
    case 'offered':
      return `offering ${habit.offered_today_count} catch-up ${
        habit.offered_today_count === 1 ? 'item' : 'items'
      } today`;
    case 'no_closed_period_yet':
      return 'no period has closed since it was enabled';
    case 'no_debt_since_anchor':
      return habit.anchor_date
        ? `debt counts only since ${habit.anchor_date}`
        : 'debt counts only since it was enabled';
    case 'all_periods_complete':
    case 'all_periods_answered':
      return 'nothing missed within reach';
    case 'nothing_outstanding':
      return 'all caught up';
    case 'no_capacity':
      return "didn't fit today's batches";
    case 'paused':
      return 'habit is paused';
    case 'snoozed':
      return 'habit is snoozed right now';
    case 'opaque_blocked':
      return 'not available for this habit type';
    default:
      return '';
  }
};

const CatchUpOverviewSection: React.FC = () => {
  const { cardContentPaddingSx } = useLayoutTokens();
  const [insight, setInsight] = useState<CheckInQueueInsightResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchInsight = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}${CHECK_IN_INSIGHT_PATH}`, {
          // Real timezone only — mirrors the queue itself (never an as_of_date).
          params: { tz_offset: new Date().getTimezoneOffset() },
        });
        if (!cancelled) setInsight(response.data);
      } catch {
        // Quiet section: an explanatory card that cannot load explains nothing,
        // and must never add an error banner to the Profile page.
        if (!cancelled) setInsight(null);
      }
    };
    fetchInsight();
    return () => {
      cancelled = true;
    };
  }, []);

  // Backfill-off habits sort last: the rows that lead are the configured ones,
  // and the trailing block IS the headline's "have backfill off" set.
  const sortedHabits = useMemo(() => {
    const habits = insight?.habits ?? [];
    return [...habits].sort(
      (a, b) =>
        (a.backfill_mode === 'none' ? 1 : 0) - (b.backfill_mode === 'none' ? 1 : 0),
    );
  }, [insight]);

  if (!insight || sortedHabits.length === 0) {
    return null;
  }

  const offCount = sortedHabits.filter((h) => h.backfill_mode === 'none').length;
  const total = sortedHabits.length;
  const headline =
    offCount > 0
      ? `${offCount} of ${total} check-in habits have backfill off`
      : `All ${total} check-in habits have backfill on`;

  return (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Typography variant="h6" gutterBottom>
          Catch-Up Overview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {headline}
        </Typography>
        {!insight.queue.snapshot_exists && (
          <Typography variant="caption" color="text.secondary" component="div">
            Today's queue hasn't been built yet — these are each habit's settings.
          </Typography>
        )}

        <Box sx={{ mt: 1.5 }}>
          {sortedHabits.map((habit) => {
            const merged =
              habit.backfill_mode === 'debt_carry_forward' &&
              habit.carryover_policy === 'merge' &&
              habit.owed_period_count > 0;
            const reason = reasonSentence(habit);
            return (
              <Box
                key={habit.habit_id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  columnGap: 1,
                  rowGap: 0.25,
                  py: 0.75,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  '&:first-of-type': { borderTop: 'none' },
                }}
              >
                <Link
                  component={RouterLink}
                  to="/habits"
                  underline="hover"
                  variant="body2"
                  sx={{ fontWeight: 600 }}
                >
                  {habit.title}
                </Link>
                <Chip
                  size="small"
                  variant="outlined"
                  color={MODE_CHIP_COLORS[habit.backfill_mode] ?? 'default'}
                  label={MODE_LABELS[habit.backfill_mode] ?? habit.backfill_mode}
                />
                {habit.backfill_mode === 'debt_carry_forward' && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={POLICY_LABELS[habit.carryover_policy] ?? habit.carryover_policy}
                  />
                )}
                {habit.reach_days != null && (
                  <Typography variant="caption" color="text.secondary">
                    {habit.reach_days} days
                  </Typography>
                )}
                {habit.owed_period_count > 0 && (
                  <Typography variant="caption" color="warning.main">
                    {habit.owed_period_count}{' '}
                    {habit.owed_period_count === 1 ? 'period' : 'periods'} ·{' '}
                    {formatOwedAmount(habit.owed_total)} owed
                    {merged ? ' · merged into one card' : ''}
                  </Typography>
                )}
                {reason && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ width: '100%' }}
                  >
                    {reason}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CatchUpOverviewSection;
